/**
 * Luật thuần của màn Cơ cấu tổ chức và ô chọn phòng ban ở màn người dùng
 * (G2-D1-17, ADR-0014, ADR-0067).
 *
 * user-service là chốt chặn thật (chu trình, độ sâu, xoá phòng ban còn người).
 * Ở đây chỉ dựng cây từ danh sách phẳng, lọc theo từ khoá, và tính trước những
 * lựa chọn chắc chắn bị từ chối để ô chọn không mời người dùng chọn chúng — đủ
 * thuần để test bằng `node:test`.
 */
import type { Department } from '../types.ts';

/** Số cấp tối đa, phòng ban gốc là cấp 1 (giá trị tạm của ADR-0014). Server vẫn là chốt chặn. */
export const DO_SAU_TOI_DA = 10;

/** Trần độ dài tên theo hợp đồng (`DepartmentCreate.name`). */
export const DAI_TEN_TOI_DA = 200;

export type LoiTenPhongBan = 'trong' | 'dai';

/** Tên đã bỏ khoảng trắng đầu cuối (server không nhận khoảng trắng ở hai đầu). */
export function kiemTenPhongBan(ten: string): LoiTenPhongBan | null {
  const t = ten.trim();
  if (!t) return 'trong';
  if (t.length > DAI_TEN_TOI_DA) return 'dai';
  return null;
}

/**
 * Một nút của cây cho bảng dạng cây của antd. `children` **vắng** ở nút lá:
 * mảng rỗng làm antd vẽ nút mở rộng cho một dòng không có gì bên dưới.
 */
export interface NutCay extends Department {
  /** Cấp của nút, gốc là 1. */
  cap: number;
  children?: NutCay[];
}

/** Bỏ dấu, chữ thường: gõ "ke toan" vẫn ra "Kế toán". */
export function chuanHoaTim(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

/**
 * Dựng cây từ danh sách phẳng. Anh em sắp theo tên (theo `locale`). Phòng ban
 * có `parent_id` trỏ tới phòng ban không có trong danh sách được treo ở gốc,
 * để không mất dòng nào; nút đã gặp không được thăm lại, nên dữ liệu hỏng có
 * chu trình cũng không làm vòng lặp chạy mãi.
 */
export function dungCay(ds: readonly Department[], locale = 'vi'): NutCay[] {
  const coTrongDs = new Set(ds.map((d) => d.id));
  const conTheoCha = new Map<string | null, Department[]>();
  for (const d of ds) {
    const cha = d.parent_id !== null && coTrongDs.has(d.parent_id) ? d.parent_id : null;
    const nhom = conTheoCha.get(cha);
    if (nhom) nhom.push(d);
    else conTheoCha.set(cha, [d]);
  }
  const theoTen = (a: Department, b: Department) => a.name.localeCompare(b.name, locale);
  const daGap = new Set<string>();

  const dung = (cha: string | null, cap: number): NutCay[] =>
    (conTheoCha.get(cha) ?? [])
      .filter((d) => !daGap.has(d.id))
      .sort(theoTen)
      .map((d) => {
        daGap.add(d.id);
        const con = dung(d.id, cap + 1);
        return con.length > 0 ? { ...d, cap, children: con } : { ...d, cap };
      });

  const goc = dung(null, 1);
  // Phòng ban nằm trong một chu trình (không tới được từ gốc): treo ở gốc.
  const sot = ds.filter((d) => !daGap.has(d.id));
  return sot.length > 0 ? [...goc, ...sot.sort(theoTen).map((d) => ({ ...d, cap: 1 }))] : goc;
}

/** Mọi id có nút con — để mở rộng cả cây. */
export function idCoCon(cay: readonly NutCay[]): string[] {
  const ra: string[] = [];
  const di = (ds: readonly NutCay[]) => {
    for (const n of ds) {
      if (n.children) {
        ra.push(n.id);
        di(n.children);
      }
    }
  };
  di(cay);
  return ra;
}

/** Số nút của cây (sau khi lọc). */
export function demNut(cay: readonly NutCay[]): number {
  return cay.reduce((tong, n) => tong + 1 + demNut(n.children ?? []), 0);
}

/**
 * Lọc cây theo từ khoá: giữ nút có tên khớp và mọi tổ tiên của nó, để người
 * dùng thấy phòng ban tìm được nằm ở đâu. Con của nút khớp **không** tự giữ.
 */
export function locCay(cay: readonly NutCay[], tuKhoa: string): NutCay[] {
  const q = chuanHoaTim(tuKhoa);
  if (!q) return [...cay];
  const loc = (ds: readonly NutCay[]): NutCay[] =>
    ds.flatMap((n) => {
      const con = loc(n.children ?? []);
      if (con.length > 0) return [{ ...n, children: con }];
      if (chuanHoaTim(n.name).includes(q)) {
        const { children: _boQua, ...la } = n;
        return [la];
      }
      return [];
    });
  return loc(cay);
}

/** Bảng id → phòng ban, dùng chung cho các phép tính đường đi. */
const theoId = (ds: readonly Department[]) => new Map(ds.map((d) => [d.id, d]));

/**
 * Chuỗi tổ tiên từ gốc tới chính `id` (gồm `id`). Dừng khi gặp lại một nút
 * (dữ liệu có chu trình) hoặc cha không có trong danh sách.
 */
export function duongToiGoc(ds: readonly Department[], id: string): Department[] {
  const bang = theoId(ds);
  const ra: Department[] = [];
  const daGap = new Set<string>();
  let hienTai = bang.get(id);
  while (hienTai && !daGap.has(hienTai.id)) {
    daGap.add(hienTai.id);
    ra.unshift(hienTai);
    hienTai = hienTai.parent_id === null ? undefined : bang.get(hienTai.parent_id);
  }
  return ra;
}

/** Tên đầy đủ dạng "Khối Tài chính / Phòng Kế toán"; `undefined` khi không có phòng ban này. */
export function tenDayDu(ds: readonly Department[], id: string, ngan = ' / '): string | undefined {
  const duong = duongToiGoc(ds, id);
  return duong.length === 0 ? undefined : duong.map((d) => d.name).join(ngan);
}

/** `id` và mọi phòng ban con cháu của nó. */
export function nhanhCua(ds: readonly Department[], id: string): Set<string> {
  const ra = new Set<string>([id]);
  let them = true;
  while (them) {
    them = false;
    for (const d of ds) {
      if (d.parent_id !== null && ra.has(d.parent_id) && !ra.has(d.id)) {
        ra.add(d.id);
        them = true;
      }
    }
  }
  return ra;
}

/** Chiều cao nhánh `id`: 0 với phòng ban lá. */
export function chieuCao(ds: readonly Department[], id: string): number {
  const conTheoCha = new Map<string, string[]>();
  for (const d of ds) {
    if (d.parent_id === null) continue;
    const nhom = conTheoCha.get(d.parent_id);
    if (nhom) nhom.push(d.id);
    else conTheoCha.set(d.parent_id, [d.id]);
  }
  const daGap = new Set<string>();
  const cao = (x: string): number => {
    if (daGap.has(x)) return 0;
    daGap.add(x);
    return Math.max(0, ...(conTheoCha.get(x) ?? []).map((c) => 1 + cao(c)));
  };
  return cao(id);
}

/** Một nút của ô chọn phòng ban dạng cây (`TreeSelect`). */
export interface NutLuaChon {
  value: string;
  /** Tên của riêng phòng ban này. */
  ten: string;
  /** Tên đầy đủ từ gốc, dạng "Khối Tài chính / Phòng Kế toán". */
  duong: string;
  /** Chọn thì cây vượt `toiDa` cấp — vẫn hiện, nhưng không chọn được. */
  quaSau: boolean;
  children?: NutLuaChon[];
}

/**
 * Cây lựa chọn cho ô chọn phòng ban: bỏ phòng ban đang sửa và con cháu của nó
 * (server trả `422 DEPARTMENT_CYCLE`); đánh `quaSau` phòng ban mà đặt nhánh đang sửa
 * (hoặc một phòng ban mới khi `dangSua` vắng) vào thì cây vượt `toiDa` cấp.
 * Anh em sắp theo tên.
 */
export function cayLuaChon(
  ds: readonly Department[],
  dangSua?: string,
  locale = 'vi',
  toiDa = DO_SAU_TOI_DA,
): NutLuaChon[] {
  const loaiTru = dangSua ? nhanhCua(ds, dangSua) : new Set<string>();
  const caoNhanh = dangSua ? chieuCao(ds, dangSua) : 0;
  const doi = (ds2: readonly NutCay[], duongCha: string): NutLuaChon[] =>
    ds2.map((n) => {
      const duong = duongCha ? `${duongCha} / ${n.name}` : n.name;
      const nut: NutLuaChon = { value: n.id, ten: n.name, duong, quaSau: n.cap + 1 + caoNhanh > toiDa };
      return n.children ? { ...nut, children: doi(n.children, duong) } : nut;
    });
  return doi(
    dungCay(
      ds.filter((d) => !loaiTru.has(d.id)),
      locale,
    ),
    '',
  );
}

/** Id các tổ tiên của `id` (không gồm chính nó), từ gốc xuống — để mở sẵn nhánh chứa giá trị đang chọn. */
export function toTienCua(ds: readonly Department[], id: string | null): string[] {
  if (id === null) return [];
  return duongToiGoc(ds, id)
    .slice(0, -1)
    .map((d) => d.id);
}

/** Còn thêm được phòng ban con dưới nút ở cấp `cap` không. */
export const themConDuoc = (cap: number, toiDa = DO_SAU_TOI_DA): boolean => cap < toiDa;

/** Xoá được ngay không: server chặn khi còn người hoặc còn phòng con (`409 DEPARTMENT_NOT_EMPTY`). */
export const xoaDuoc = (d: Pick<Department, 'user_count' | 'child_count'>): boolean =>
  d.user_count === 0 && d.child_count === 0;

/**
 * Lỗi theo từng trường của mọi mã lỗi có `errors[].pointer` (không chỉ
 * `INVALID_ARGUMENT`): `422 DEPARTMENT_CYCLE`, `404 DEPARTMENT_NOT_FOUND` trỏ
 * `#/parent_id`. Khoá bỏ tiền tố `#`: `{ '/parent_id': '…' }`.
 */
export function loiTheoConTro(details: unknown): Record<string, string> {
  if (!Array.isArray(details)) return {};
  const ra: Record<string, string> = {};
  for (const loi of details as Array<{ pointer?: string; detail?: string }>) {
    if (loi?.pointer && loi.detail) ra[loi.pointer.replace(/^#/, '')] = loi.detail;
  }
  return ra;
}
